/**
 * AdminNotificationConfig — admin panel for system-wide notification settings.
 *
 * Renders:
 * - Channel enable/disable toggles for organization-level config
 * - Default preferences template editor
 * - Delivery statistics (sent/failed per channel, last 7 days)
 *
 * Restricted to admin role.
 *
 * Requirements: 16.1, 16.2, 16.4, 16.6, 16.9
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import * as api from '../../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChannelConfig {
  readonly email_graph: boolean;
  readonly email_google: boolean;
  readonly teams: boolean;
  readonly slack: boolean;
}

interface DefaultPreferencesTemplate {
  readonly 'card.assigned': ChannelToggles;
  readonly 'board.invite.created': ChannelToggles;
  readonly 'comment.mention': ChannelToggles;
  readonly 'card.due.approaching': ChannelToggles;
  readonly 'card.moved': ChannelToggles;
}

interface ChannelToggles {
  readonly inApp: boolean;
  readonly email: boolean;
  readonly teams: boolean;
  readonly slack: boolean;
}

interface DeliveryStats {
  readonly channels: Record<string, { sent: number; failed: number }>;
  readonly period: string;
}

/** Masked integration credentials as returned by the GET response */
interface MaskedIntegrationCredentials {
  readonly email_graph?: {
    readonly tenantId: string;
    readonly clientId: string;
    readonly clientSecret: string;
    readonly senderEmail: string;
  };
  readonly email_google?: {
    readonly serviceAccountEmail: string;
    readonly privateKey: string;
  };
  readonly slack?: {
    readonly webhookUrl: string;
  };
  readonly teams?: {
    readonly webhookUrl?: string;
    readonly teamId?: string;
    readonly channelId?: string;
  };
}

/** Response from GET /v1/admin/teams-integration */
interface TeamsIntegrationConfig {
  readonly configured: boolean;
  readonly tenantId?: string;
  readonly clientId?: string;
  readonly clientSecretMasked?: string;
  readonly allowlistMode?: string;
  readonly allowlist?: string[];
  readonly allowlistNames?: Record<string, string>;
  readonly configuredAt?: string;
}

/** Team entry from GET /v1/admin/teams-integration/teams */
interface AdminTeamEntry {
  readonly id: string;
  readonly displayName: string;
}

interface AdminNotificationConfigData {
  readonly channels: ChannelConfig;
  readonly defaults: DefaultPreferencesTemplate;
  readonly integrations?: MaskedIntegrationCredentials;
  readonly stats?: DeliveryStats;
}

/** Form state for the Global Integrations credential entry form */
export interface IntegrationFormState {
  email_graph: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
    senderEmail: string;
  };
  email_google: {
    serviceAccountEmail: string;
    privateKey: string;
  };
  slack: {
    webhookUrl: string;
  };
  teams: {
    webhookUrl: string;
    teamId: string;
    channelId: string;
  };
}

type EventTypeKey = keyof DefaultPreferencesTemplate;
type ChannelKey = keyof ChannelToggles;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Sentinel value sent to the backend for secret fields that the user has not modified.
 * The backend recognizes this and retains the existing stored value.
 */
export const UNCHANGED_SENTINEL = '__UNCHANGED__';

/** Mask string returned by the backend for secret fields */
const MASK_STRING = '••••••••';

/** Empty default state for the integrations form */
const DEFAULT_INTEGRATIONS: IntegrationFormState = {
  email_graph: { tenantId: '', clientId: '', clientSecret: '', senderEmail: '' },
  email_google: { serviceAccountEmail: '', privateKey: '' },
  slack: { webhookUrl: '' },
  teams: { webhookUrl: '', teamId: '', channelId: '' },
};

const CHANNEL_LABELS: Record<keyof ChannelConfig, string> = {
  email_graph: 'Email (Microsoft Graph)',
  email_google: 'Email (Google)',
  teams: 'Microsoft Teams',
  slack: 'Slack',
};

const EVENT_TYPE_LABELS: Record<EventTypeKey, string> = {
  'card.assigned': 'Card assigned',
  'board.invite.created': 'Board invitation',
  'comment.mention': 'Comment mention',
  'card.due.approaching': 'Due date approaching',
  'card.moved': 'Card moved',
};

const PREF_CHANNEL_LABELS: Record<ChannelKey, string> = {
  inApp: 'In-App',
  email: 'Email',
  teams: 'Teams',
  slack: 'Slack',
};

const DEFAULT_CONFIG: AdminNotificationConfigData = {
  channels: { email_graph: true, email_google: true, teams: false, slack: true },
  defaults: {
    'card.assigned': { inApp: true, email: false, teams: false, slack: false },
    'board.invite.created': { inApp: true, email: false, teams: false, slack: false },
    'comment.mention': { inApp: true, email: false, teams: false, slack: false },
    'card.due.approaching': { inApp: true, email: false, teams: false, slack: false },
    'card.moved': { inApp: true, email: false, teams: false, slack: false },
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converts masked integration credentials from the GET response into form state.
 * Secret fields that come back masked are set to UNCHANGED_SENTINEL so the backend
 * knows to retain the existing stored value if the user doesn't modify them.
 */
function buildIntegrationFormState(
  masked: MaskedIntegrationCredentials | undefined,
): IntegrationFormState {
  if (!masked) return DEFAULT_INTEGRATIONS;

  return {
    email_graph: {
      tenantId: masked.email_graph?.tenantId ?? '',
      clientId: masked.email_graph?.clientId ?? '',
      clientSecret: masked.email_graph?.clientSecret === MASK_STRING
        ? UNCHANGED_SENTINEL
        : (masked.email_graph?.clientSecret ?? ''),
      senderEmail: masked.email_graph?.senderEmail ?? '',
    },
    email_google: {
      serviceAccountEmail: masked.email_google?.serviceAccountEmail ?? '',
      privateKey: masked.email_google?.privateKey === MASK_STRING
        ? UNCHANGED_SENTINEL
        : (masked.email_google?.privateKey ?? ''),
    },
    slack: {
      webhookUrl: masked.slack?.webhookUrl?.endsWith(MASK_STRING)
        ? UNCHANGED_SENTINEL
        : (masked.slack?.webhookUrl ?? ''),
    },
    teams: {
      webhookUrl: masked.teams?.webhookUrl?.endsWith(MASK_STRING)
        ? UNCHANGED_SENTINEL
        : (masked.teams?.webhookUrl ?? ''),
      teamId: masked.teams?.teamId ?? '',
      channelId: masked.teams?.channelId ?? '',
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminNotificationConfig(): React.ReactElement {
  const [config, setConfig] = useState<AdminNotificationConfigData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [integrations, setIntegrations] = useState<IntegrationFormState>(DEFAULT_INTEGRATIONS);
  const [integrationErrors, setIntegrationErrors] = useState<string[]>([]);
  const [integrationSuccess, setIntegrationSuccess] = useState<string | null>(null);
  const [isSavingIntegrations, setIsSavingIntegrations] = useState(false);
  const [testingChannel, setTestingChannel] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ channel: string; success: boolean; message: string } | null>(null);

  // Teams Integration (dedicated credential form)
  const [teamsIntegration, setTeamsIntegration] = useState<TeamsIntegrationConfig | null>(null);
  const [teamsCredForm, setTeamsCredForm] = useState({ tenantId: '', clientId: '', clientSecret: '' });
  const [teamsCredSaving, setTeamsCredSaving] = useState(false);
  const [teamsCredTesting, setTeamsCredTesting] = useState(false);
  const [teamsCredMessage, setTeamsCredMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Teams Allowlist
  const [allowlistMode, setAllowlistMode] = useState<'allow_all' | 'restricted'>('allow_all');
  const [allowlist, setAllowlist] = useState<string[]>([]);
  const [allowlistNames, setAllowlistNames] = useState<Record<string, string>>({});
  const [adminTeamsList, setAdminTeamsList] = useState<AdminTeamEntry[]>([]);
  const [adminTeamsLoading, setAdminTeamsLoading] = useState(false);
  const [allowlistSaving, setAllowlistSaving] = useState(false);

  const fetchConfig = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<AdminNotificationConfigData>('/v1/admin/notifications');
      // Validate that the response has the expected shape; fall back to defaults if not
      if (data && data.channels && data.defaults) {
        setConfig(data);
        setIntegrations(buildIntegrationFormState(data.integrations));
      } else {
        setConfig(DEFAULT_CONFIG);
        setIntegrations(DEFAULT_INTEGRATIONS);
      }
    } catch {
      // Use defaults if fetch fails (e.g., first-time setup)
      setConfig(DEFAULT_CONFIG);
      setIntegrations(DEFAULT_INTEGRATIONS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  /** Fetches the dedicated Teams integration config on mount */
  const fetchTeamsIntegration = useCallback(async (): Promise<void> => {
    try {
      const data = await api.get<TeamsIntegrationConfig>('/v1/admin/teams-integration');
      setTeamsIntegration(data);
      if (data.configured) {
        setTeamsCredForm({
          tenantId: data.tenantId ?? '',
          clientId: data.clientId ?? '',
          clientSecret: '',
        });
      }
      // Populate allowlist state from response
      setAllowlistMode((data.allowlistMode === 'restricted') ? 'restricted' : 'allow_all');
      setAllowlist(data.allowlist ?? []);
      setAllowlistNames(data.allowlistNames ?? {});
    } catch {
      // Not yet configured — leave defaults
      setTeamsIntegration({ configured: false });
    }
  }, []);

  useEffect(() => {
    fetchTeamsIntegration();
  }, [fetchTeamsIntegration]);

  const handleChannelToggle = useCallback(
    async (channel: keyof ChannelConfig, enabled: boolean): Promise<void> => {
      if (!config) return;
      const previousConfig = config;
      const updatedConfig: AdminNotificationConfigData = {
        ...config,
        channels: { ...config.channels, [channel]: enabled },
      };

      setConfig(updatedConfig);
      setError(null);
      setSuccessMessage(null);
      setIsSaving(true);

      try {
        await api.put('/v1/admin/notifications', {
          channels: updatedConfig.channels,
          defaults: updatedConfig.defaults,
        });
        setSuccessMessage('Channel configuration saved');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err: unknown) {
        setConfig(previousConfig);
        const message = err instanceof Error ? err.message : 'Failed to save configuration';
        setError(message);
      } finally {
        setIsSaving(false);
      }
    },
    [config],
  );

  const handleDefaultToggle = useCallback(
    async (eventType: EventTypeKey, channel: ChannelKey, checked: boolean): Promise<void> => {
      if (!config) return;
      const previousConfig = config;
      const updatedDefaults: DefaultPreferencesTemplate = {
        ...config.defaults,
        [eventType]: { ...config.defaults[eventType], [channel]: checked },
      };
      const updatedConfig: AdminNotificationConfigData = {
        ...config,
        defaults: updatedDefaults,
      };

      setConfig(updatedConfig);
      setError(null);
      setSuccessMessage(null);
      setIsSaving(true);

      try {
        await api.put('/v1/admin/notifications', {
          channels: updatedConfig.channels,
          defaults: updatedConfig.defaults,
        });
        setSuccessMessage('Default preferences saved');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err: unknown) {
        setConfig(previousConfig);
        const message = err instanceof Error ? err.message : 'Failed to save default preferences';
        setError(message);
      } finally {
        setIsSaving(false);
      }
    },
    [config],
  );

  /** Validates integration form fields. Returns error messages (empty if valid). */
  const validateIntegrationForm = useCallback((): string[] => {
    const errors: string[] = [];
    const g = integrations.email_graph;
    const hasAnyGraph = g.tenantId || g.clientId || (g.clientSecret && g.clientSecret !== UNCHANGED_SENTINEL);
    if (hasAnyGraph) {
      if (!g.tenantId) errors.push('Microsoft Graph: Tenant ID is required');
      if (!g.clientId) errors.push('Microsoft Graph: Client ID is required');
      if (!g.clientSecret) errors.push('Microsoft Graph: Client Secret is required');
    }

    const goog = integrations.email_google;
    const hasAnyGoogle = goog.serviceAccountEmail || (goog.privateKey && goog.privateKey !== UNCHANGED_SENTINEL);
    if (hasAnyGoogle) {
      if (!goog.serviceAccountEmail) errors.push('Google: Service Account Email is required');
      if (!goog.privateKey) errors.push('Google: Private Key is required');
    }

    const sl = integrations.slack;
    if (sl.webhookUrl && sl.webhookUrl !== UNCHANGED_SENTINEL) {
      if (!sl.webhookUrl.startsWith('https://')) {
        errors.push('Slack: Webhook URL must start with https://');
      }
    }

    const tm = integrations.teams;
    if (tm.teamId || tm.channelId) {
      if (!tm.teamId.trim()) errors.push('Teams: Team ID is required');
      if (!tm.channelId.trim()) errors.push('Teams: Channel ID is required');
    }

    return errors;
  }, [integrations]);

  /** Saves integration credentials via PUT /v1/admin/notifications */
  const handleSaveIntegrations = useCallback(async (): Promise<void> => {
    if (!config) return;

    const errors = validateIntegrationForm();
    if (errors.length > 0) {
      setIntegrationErrors(errors);
      setIntegrationSuccess(null);
      return;
    }

    setIntegrationErrors([]);
    setIntegrationSuccess(null);
    setIsSavingIntegrations(true);

    // Only include channels that have actual data (not all-empty defaults)
    const integrationsPayload: Record<string, unknown> = {};

    const g = integrations.email_graph;
    if (g.tenantId || g.clientId || g.clientSecret) {
      integrationsPayload['email_graph'] = g;
    }

    const goog = integrations.email_google;
    if (goog.serviceAccountEmail || goog.privateKey) {
      integrationsPayload['email_google'] = goog;
    }

    const sl = integrations.slack;
    if (sl.webhookUrl) {
      integrationsPayload['slack'] = sl;
    }

    const tm = integrations.teams;
    if (tm.webhookUrl || tm.teamId || tm.channelId) {
      // Only send the relevant fields for Teams
      const teamsPayload: Record<string, string> = {};
      if (tm.webhookUrl) teamsPayload['webhookUrl'] = tm.webhookUrl;
      if (tm.teamId) teamsPayload['teamId'] = tm.teamId;
      if (tm.channelId) teamsPayload['channelId'] = tm.channelId;
      integrationsPayload['teams'] = teamsPayload;
    }

    try {
      await api.put('/v1/admin/notifications', {
        channels: config.channels,
        defaults: config.defaults,
        integrations: integrationsPayload,
      });
      setIntegrationSuccess('Integration credentials saved successfully');
      setTimeout(() => setIntegrationSuccess(null), 3000);
      // Refresh to get updated masked values
      fetchConfig();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      const message = apiErr?.message ?? 'Failed to save integration credentials';
      setIntegrationErrors([message]);
    } finally {
      setIsSavingIntegrations(false);
    }
  }, [config, integrations, validateIntegrationForm, fetchConfig]);

  /** Tests connectivity for a specific integration channel */
  const handleTestConnection = useCallback(async (channel: 'email_graph' | 'slack' | 'teams'): Promise<void> => {
    setTestingChannel(channel);
    setTestResult(null);

    let payload: Record<string, unknown> = { channel };

    if (channel === 'email_graph') {
      const g = integrations.email_graph;
      if (!g.tenantId || !g.clientId || !g.clientSecret) {
        setTestResult({ channel, success: false, message: 'Fill in all Microsoft Graph fields before testing' });
        setTestingChannel(null);
        return;
      }
      // Don't send the sentinel for test — the test needs real credentials
      if (g.clientSecret === UNCHANGED_SENTINEL) {
        setTestResult({ channel, success: false, message: 'Enter the client secret to test (masked values cannot be tested)' });
        setTestingChannel(null);
        return;
      }
      payload = { ...payload, tenantId: g.tenantId, clientId: g.clientId, clientSecret: g.clientSecret };
    } else if (channel === 'slack') {
      const sl = integrations.slack;
      if (!sl.webhookUrl || sl.webhookUrl === UNCHANGED_SENTINEL) {
        setTestResult({ channel, success: false, message: 'Enter the Slack webhook URL to test' });
        setTestingChannel(null);
        return;
      }
      payload = { ...payload, webhookUrl: sl.webhookUrl };
    } else if (channel === 'teams') {
      const tm = integrations.teams;
      if (!tm.teamId || !tm.channelId) {
        setTestResult({ channel, success: false, message: 'Enter both Team ID and Channel ID to test' });
        setTestingChannel(null);
        return;
      }
      payload = { ...payload, teamId: tm.teamId, channelId: tm.channelId };
    }

    try {
      const result = await api.post<{ success: boolean; message: string; errorCode?: string }>('/v1/admin/notifications/test', payload);
      setTestResult({ channel, success: result.success, message: result.message });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test request failed';
      setTestResult({ channel, success: false, message });
    } finally {
      setTestingChannel(null);
    }
  }, [integrations]);

  /** Saves Teams integration credentials via PUT /v1/admin/teams-integration */
  const handleSaveTeamsCredentials = useCallback(async (): Promise<void> => {
    setTeamsCredMessage(null);
    setTeamsCredSaving(true);

    // Determine what to send for clientSecret
    const secretToSend = teamsCredForm.clientSecret
      ? teamsCredForm.clientSecret
      : teamsIntegration?.configured && teamsIntegration.clientSecretMasked
        ? teamsIntegration.clientSecretMasked
        : '';

    try {
      await api.put('/v1/admin/teams-integration', {
        tenantId: teamsCredForm.tenantId,
        clientId: teamsCredForm.clientId,
        clientSecret: secretToSend,
        allowlistMode: 'allow_all',
        allowlist: [],
      });
      setTeamsCredMessage({ type: 'success', text: 'Credentials saved' });
      // Refresh the integration state
      fetchTeamsIntegration();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save credentials';
      setTeamsCredMessage({ type: 'error', text: message });
    } finally {
      setTeamsCredSaving(false);
    }
  }, [teamsCredForm, teamsIntegration, fetchTeamsIntegration]);

  /** Tests Teams integration credentials via POST /v1/admin/teams-integration/test */
  const handleTestTeamsConnection = useCallback(async (): Promise<void> => {
    setTeamsCredMessage(null);
    setTeamsCredTesting(true);

    try {
      const result = await api.post<{ success: boolean; message: string; teamCount?: number }>('/v1/admin/teams-integration/test', {});
      if (result.success) {
        setTeamsCredMessage({ type: 'success', text: result.message });
      } else {
        setTeamsCredMessage({ type: 'error', text: result.message });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test request failed';
      setTeamsCredMessage({ type: 'error', text: message });
    } finally {
      setTeamsCredTesting(false);
    }
  }, []);

  /** Loads available teams from the admin endpoint for the allowlist builder */
  const handleLoadAdminTeams = useCallback(async (): Promise<void> => {
    setAdminTeamsLoading(true);
    try {
      const result = await api.get<{ teams: AdminTeamEntry[] }>('/v1/admin/teams-integration/teams');
      setAdminTeamsList(result.teams ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load teams';
      setTeamsCredMessage({ type: 'error', text: message });
    } finally {
      setAdminTeamsLoading(false);
    }
  }, []);

  /** Saves the allowlist configuration via PUT /v1/admin/teams-integration */
  const handleSaveAllowlist = useCallback(async (): Promise<void> => {
    setAllowlistSaving(true);
    setTeamsCredMessage(null);

    // Use current form tenantId/clientId; send masked sentinel for clientSecret since unchanged
    const secretToSend = teamsIntegration?.clientSecretMasked ?? UNCHANGED_SENTINEL;

    try {
      await api.put('/v1/admin/teams-integration', {
        tenantId: teamsCredForm.tenantId,
        clientId: teamsCredForm.clientId,
        clientSecret: secretToSend,
        allowlistMode,
        allowlist,
        allowlistNames,
      });
      setTeamsCredMessage({ type: 'success', text: 'Allowlist saved' });
      fetchTeamsIntegration();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save allowlist';
      setTeamsCredMessage({ type: 'error', text: message });
    } finally {
      setAllowlistSaving(false);
    }
  }, [teamsIntegration, teamsCredForm, allowlistMode, allowlist, allowlistNames, fetchTeamsIntegration]);

  /** Updates a nested integration field value */
  const handleIntegrationChange = useCallback(
    (channel: keyof IntegrationFormState, field: string, value: string): void => {
      setIntegrations((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], [field]: value },
      }));
    },
    [],
  );

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading notification configuration" className="py-4">
        <span className="text-sm text-muted-foreground">Loading notification configuration…</span>
      </div>
    );
  }

  if (!config) return <></>;

  return (
    <div className="space-y-8">
      {/* Status messages */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {/* Channel Configuration */}
      <section aria-labelledby="admin-channels-heading">
        <h3 id="admin-channels-heading" className="text-sm font-semibold mb-3">
          Delivery Channels
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Enable or disable notification channels at the organization level. Disabled channels will not
          deliver notifications regardless of individual user preferences.
        </p>
        <div className="space-y-3">
          {(Object.keys(CHANNEL_LABELS) as Array<keyof ChannelConfig>).map((channelKey) => (
            <div key={channelKey} className="flex items-center gap-3">
              <Checkbox
                id={`admin-channel-${channelKey}`}
                checked={config.channels[channelKey]}
                onCheckedChange={(checked) => handleChannelToggle(channelKey, checked === true)}
                disabled={isSaving}
                aria-label={`Enable ${CHANNEL_LABELS[channelKey]}`}
              />
              <Label htmlFor={`admin-channel-${channelKey}`} className="text-sm">
                {CHANNEL_LABELS[channelKey]}
              </Label>
            </div>
          ))}
        </div>
      </section>

      {/* Default Preferences Template */}
      <section aria-labelledby="admin-defaults-heading">
        <h3 id="admin-defaults-heading" className="text-sm font-semibold mb-3">
          Default Preferences for New Users
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Configure the initial notification preferences applied to new user accounts.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="Default notification preferences">
            <thead>
              <tr className="border-b">
                <th className="pb-2 pr-4 text-left font-medium" scope="col">Event</th>
                {(Object.keys(PREF_CHANNEL_LABELS) as ChannelKey[]).map((ch) => (
                  <th key={ch} className="pb-2 px-3 text-center font-medium" scope="col">
                    {PREF_CHANNEL_LABELS[ch]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(Object.keys(EVENT_TYPE_LABELS) as EventTypeKey[]).map((evt) => (
                <tr key={evt} className="border-b last:border-b-0">
                  <td className="py-3 pr-4 font-medium">{EVENT_TYPE_LABELS[evt]}</td>
                  {(Object.keys(PREF_CHANNEL_LABELS) as ChannelKey[]).map((ch) => (
                    <td key={ch} className="py-3 px-3 text-center">
                      <Checkbox
                        id={`admin-default-${evt}-${ch}`}
                        checked={config.defaults[evt][ch]}
                        onCheckedChange={(checked) => handleDefaultToggle(evt, ch, checked === true)}
                        disabled={isSaving}
                        aria-label={`Default ${EVENT_TYPE_LABELS[evt]} via ${PREF_CHANNEL_LABELS[ch]}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Delivery Statistics */}
      <section aria-labelledby="admin-stats-heading">
        <h3 id="admin-stats-heading" className="text-sm font-semibold mb-3">
          Delivery Statistics (Last 7 Days)
        </h3>
        {config.stats && config.stats.channels && Object.keys(config.stats.channels).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Delivery statistics">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 pr-4 text-left font-medium" scope="col">Channel</th>
                  <th className="pb-2 px-3 text-center font-medium" scope="col">Sent</th>
                  <th className="pb-2 px-3 text-center font-medium" scope="col">Failed</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(config.stats.channels).map(([channel, data]) => (
                  <tr key={channel} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{CHANNEL_LABELS[channel as keyof ChannelConfig] ?? channel}</td>
                    <td className="py-2 px-3 text-center text-success">{data.sent}</td>
                    <td className="py-2 px-3 text-center text-destructive">{data.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No delivery statistics available yet. Statistics will appear once notifications begin sending.
          </p>
        )}
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={fetchConfig}
          aria-label="Refresh delivery statistics"
        >
          Refresh
        </Button>
      </section>

      {/* Global Integrations */}
      <section aria-labelledby="admin-integrations-heading">
        <h3 id="admin-integrations-heading" className="text-sm font-semibold mb-3">
          Global Integrations
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Configure credentials for each delivery channel. Secret fields are stored securely and masked
          after saving. Leave unchanged fields as-is to retain existing credentials.
        </p>

        {integrationErrors.length > 0 && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              <ul className="list-disc pl-4 space-y-1">
                {integrationErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        {integrationSuccess && (
          <Alert variant="success" className="mb-4">
            <AlertDescription>{integrationSuccess}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Microsoft Graph */}
          <details className="group border rounded-md">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md">
              Email (Microsoft Graph)
            </summary>
            <div className="px-4 pb-4 pt-2 space-y-3">
              <div>
                <Label htmlFor="integration-graph-tenantId" className="text-xs">
                  Tenant ID
                </Label>
                <Input
                  id="integration-graph-tenantId"
                  type="text"
                  value={integrations.email_graph.tenantId}
                  onChange={(e) => handleIntegrationChange('email_graph', 'tenantId', e.target.value)}
                  placeholder="e.g. abc-123-def"
                  disabled={isSavingIntegrations}
                />
              </div>
              <div>
                <Label htmlFor="integration-graph-clientId" className="text-xs">
                  Application ID
                </Label>
                <Input
                  id="integration-graph-clientId"
                  type="text"
                  value={integrations.email_graph.clientId}
                  onChange={(e) => handleIntegrationChange('email_graph', 'clientId', e.target.value)}
                  placeholder="e.g. app-client-id"
                  disabled={isSavingIntegrations}
                />
              </div>
              <div>
                <Label htmlFor="integration-graph-clientSecret" className="text-xs">
                  Application Secret
                </Label>
                <Input
                  id="integration-graph-clientSecret"
                  type="password"
                  value={integrations.email_graph.clientSecret === UNCHANGED_SENTINEL ? '' : integrations.email_graph.clientSecret}
                  onChange={(e) => handleIntegrationChange('email_graph', 'clientSecret', e.target.value)}
                  placeholder={integrations.email_graph.clientSecret === UNCHANGED_SENTINEL ? '••••••••' : ''}
                  disabled={isSavingIntegrations}
                />
              </div>
              <div>
                <Label htmlFor="integration-graph-senderEmail" className="text-xs">
                  Sender Email (shared mailbox)
                </Label>
                <Input
                  id="integration-graph-senderEmail"
                  type="email"
                  value={integrations.email_graph.senderEmail}
                  onChange={(e) => handleIntegrationChange('email_graph', 'senderEmail', e.target.value)}
                  placeholder="e.g. notifications@yourorg.com"
                  disabled={isSavingIntegrations}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  The mailbox notifications are sent from. Must be a user or shared mailbox the app has Mail.Send permission for.
                </p>
              </div>
              {testResult && testResult.channel === 'email_graph' && (
                <Alert variant={testResult.success ? 'success' : 'destructive'}>
                  <AlertDescription>{testResult.message}</AlertDescription>
                </Alert>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestConnection('email_graph')}
                disabled={testingChannel !== null || isSavingIntegrations}
                aria-label="Test Microsoft Graph connection"
              >
                {testingChannel === 'email_graph' ? 'Testing…' : 'Test Connection'}
              </Button>
            </div>
          </details>

          {/* Google */}
          <details className="group border rounded-md">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md">
              Email (Google)
            </summary>
            <div className="px-4 pb-4 pt-2 space-y-3">
              <div>
                <Label htmlFor="integration-google-serviceAccountEmail" className="text-xs">
                  Service Account Email
                </Label>
                <Input
                  id="integration-google-serviceAccountEmail"
                  type="text"
                  value={integrations.email_google.serviceAccountEmail}
                  onChange={(e) => handleIntegrationChange('email_google', 'serviceAccountEmail', e.target.value)}
                  placeholder="e.g. service@project.iam.gserviceaccount.com"
                  disabled={isSavingIntegrations}
                />
              </div>
              <div>
                <Label htmlFor="integration-google-privateKey" className="text-xs">
                  Private Key
                </Label>
                <Input
                  id="integration-google-privateKey"
                  type="password"
                  value={integrations.email_google.privateKey === UNCHANGED_SENTINEL ? '' : integrations.email_google.privateKey}
                  onChange={(e) => handleIntegrationChange('email_google', 'privateKey', e.target.value)}
                  placeholder={integrations.email_google.privateKey === UNCHANGED_SENTINEL ? '••••••••' : ''}
                  disabled={isSavingIntegrations}
                />
              </div>
            </div>
          </details>

          {/* Slack */}
          <details className="group border rounded-md">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md">
              Slack
            </summary>
            <div className="px-4 pb-4 pt-2 space-y-3">
              <div>
                <Label htmlFor="integration-slack-webhookUrl" className="text-xs">
                  Webhook URL
                </Label>
                <Input
                  id="integration-slack-webhookUrl"
                  type="text"
                  value={integrations.slack.webhookUrl === UNCHANGED_SENTINEL ? '' : integrations.slack.webhookUrl}
                  onChange={(e) => handleIntegrationChange('slack', 'webhookUrl', e.target.value)}
                  placeholder={integrations.slack.webhookUrl === UNCHANGED_SENTINEL ? '••••••••' : 'https://hooks.slack.com/services/...'}
                  disabled={isSavingIntegrations}
                />
              </div>
              {testResult && testResult.channel === 'slack' && (
                <Alert variant={testResult.success ? 'success' : 'destructive'}>
                  <AlertDescription>{testResult.message}</AlertDescription>
                </Alert>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestConnection('slack')}
                disabled={testingChannel !== null || isSavingIntegrations}
                aria-label="Test Slack connection"
              >
                {testingChannel === 'slack' ? 'Testing…' : 'Test Connection'}
              </Button>
            </div>
          </details>

          {/* Teams — Dedicated Integration Credentials */}
          <details className="group border rounded-md">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md">
              Microsoft Teams
            </summary>
            <div className="px-4 pb-4 pt-2 space-y-3">
              {/* Setup Guide */}
              {!teamsIntegration?.configured ? (
                <div className="border border-blue-200 bg-blue-50 rounded-md p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-blue-900">Setup Guide</h4>
                  <ol className="list-decimal list-inside text-xs text-blue-800 space-y-1">
                    <li>Register an Entra ID (Azure AD) application, or use an existing one.</li>
                    <li>Grant API permissions: <code className="text-xs bg-blue-100 px-1 rounded">Team.ReadBasic.All</code> and <code className="text-xs bg-blue-100 px-1 rounded">Channel.ReadBasic.All</code> (Application type, with admin consent).</li>
                    <li>Deploy/install the app to your Teams tenant (org-wide or specific teams).</li>
                    <li>Enter the Tenant ID, Client ID, and Client Secret below.</li>
                  </ol>
                </div>
              ) : (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Setup guide
                  </summary>
                  <ol className="list-decimal list-inside text-xs text-muted-foreground space-y-1 mt-2 pl-2">
                    <li>Register an Entra ID (Azure AD) application, or use an existing one.</li>
                    <li>Grant API permissions: <code className="text-xs bg-muted px-1 rounded">Team.ReadBasic.All</code> and <code className="text-xs bg-muted px-1 rounded">Channel.ReadBasic.All</code> (Application type, with admin consent).</li>
                    <li>Deploy/install the app to your Teams tenant (org-wide or specific teams).</li>
                    <li>Enter the Tenant ID, Client ID, and Client Secret below.</li>
                  </ol>
                </details>
              )}

              {/* Credential Fields */}
              <div>
                <Label htmlFor="integration-teams-tenantId" className="text-xs">
                  Tenant ID
                </Label>
                <Input
                  id="integration-teams-tenantId"
                  type="text"
                  value={teamsCredForm.tenantId}
                  onChange={(e) => setTeamsCredForm((prev) => ({ ...prev, tenantId: e.target.value }))}
                  placeholder="e.g. abc-123-def"
                  disabled={teamsCredSaving}
                />
              </div>
              <div>
                <Label htmlFor="integration-teams-clientId" className="text-xs">
                  Client ID (Application ID)
                </Label>
                <Input
                  id="integration-teams-clientId"
                  type="text"
                  value={teamsCredForm.clientId}
                  onChange={(e) => setTeamsCredForm((prev) => ({ ...prev, clientId: e.target.value }))}
                  placeholder="e.g. app-456-ghi"
                  disabled={teamsCredSaving}
                />
              </div>
              <div>
                <Label htmlFor="integration-teams-clientSecret" className="text-xs">
                  Client Secret
                </Label>
                <Input
                  id="integration-teams-clientSecret"
                  type="password"
                  value={teamsCredForm.clientSecret}
                  onChange={(e) => setTeamsCredForm((prev) => ({ ...prev, clientSecret: e.target.value }))}
                  placeholder={teamsIntegration?.configured ? MASK_STRING : ''}
                  disabled={teamsCredSaving}
                />
              </div>
              {teamsCredMessage && (
                <Alert variant={teamsCredMessage.type === 'success' ? 'success' : 'destructive'}>
                  <AlertDescription>{teamsCredMessage.text}</AlertDescription>
                </Alert>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveTeamsCredentials}
                  disabled={teamsCredSaving || teamsCredTesting}
                  aria-label="Save Teams credentials"
                >
                  {teamsCredSaving ? 'Saving…' : 'Save Credentials'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestTeamsConnection}
                  disabled={teamsCredSaving || teamsCredTesting || !teamsIntegration?.configured}
                  aria-label="Test Teams connection"
                >
                  {teamsCredTesting ? 'Testing…' : 'Test Connection'}
                </Button>
              </div>

              {/* Allowlist UI — only visible when configured */}
              {teamsIntegration?.configured && (
                <div className="border-t pt-4 mt-4 space-y-3">
                  <h4 className="text-sm font-semibold">Team Access</h4>
                  <fieldset className="space-y-2">
                    <legend className="sr-only">Team access mode</legend>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="allowlist-mode-all"
                        name="allowlistMode"
                        value="allow_all"
                        checked={allowlistMode === 'allow_all'}
                        onChange={() => setAllowlistMode('allow_all')}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="allowlist-mode-all" className="text-sm">
                        Allow all teams
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="allowlist-mode-restricted"
                        name="allowlistMode"
                        value="restricted"
                        checked={allowlistMode === 'restricted'}
                        onChange={() => setAllowlistMode('restricted')}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="allowlist-mode-restricted" className="text-sm">
                        Restrict to specific teams
                      </Label>
                    </div>
                  </fieldset>

                  {allowlistMode === 'restricted' && (
                    <div className="space-y-3 pl-2">
                      {/* Load teams button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLoadAdminTeams}
                        disabled={adminTeamsLoading}
                        aria-label="Load available teams"
                      >
                        {adminTeamsLoading ? 'Loading…' : 'Load Teams'}
                      </Button>

                      {/* Team picker (select + add) */}
                      {adminTeamsList.length > 0 && (
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label htmlFor="allowlist-team-select" className="text-xs">
                              Available Teams
                            </Label>
                            <select
                              id="allowlist-team-select"
                              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              defaultValue=""
                            >
                              <option value="" disabled>Select a team…</option>
                              {adminTeamsList
                                .filter((t) => !allowlist.includes(t.id))
                                .map((team) => (
                                  <option key={team.id} value={team.id}>
                                    {team.displayName}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const selectEl = document.getElementById('allowlist-team-select') as HTMLSelectElement | null;
                              if (!selectEl || !selectEl.value) return;
                              const teamId = selectEl.value;
                              const team = adminTeamsList.find((t) => t.id === teamId);
                              if (team && !allowlist.includes(teamId)) {
                                setAllowlist((prev) => [...prev, teamId]);
                                setAllowlistNames((prev) => ({ ...prev, [teamId]: team.displayName }));
                              }
                              selectEl.value = '';
                            }}
                            aria-label="Add team to allowlist"
                          >
                            Add
                          </Button>
                        </div>
                      )}

                      {/* Current allowlist */}
                      {allowlist.length > 0 && (
                        <div className="space-y-1">
                          <Label className="text-xs">Allowed Teams</Label>
                          <ul className="space-y-1">
                            {allowlist.map((teamId) => (
                              <li key={teamId} className="flex items-center justify-between rounded border px-3 py-1 text-sm">
                                <span>{allowlistNames[teamId] ?? teamId}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setAllowlist((prev) => prev.filter((id) => id !== teamId));
                                    setAllowlistNames((prev) => {
                                      const next = { ...prev };
                                      delete next[teamId];
                                      return next;
                                    });
                                  }}
                                  aria-label={`Remove ${allowlistNames[teamId] ?? teamId}`}
                                  className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                                >
                                  Remove
                                </Button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    size="sm"
                    onClick={handleSaveAllowlist}
                    disabled={allowlistSaving}
                    aria-label="Save allowlist configuration"
                  >
                    {allowlistSaving ? 'Saving…' : 'Save Allowlist'}
                  </Button>
                </div>
              )}
            </div>
          </details>
        </div>

        <Button
          className="mt-4"
          onClick={handleSaveIntegrations}
          disabled={isSavingIntegrations}
          aria-label="Save integration credentials"
        >
          {isSavingIntegrations ? 'Saving…' : 'Save Integrations'}
        </Button>
      </section>
    </div>
  );
}
